import { inject, Injectable } from '@angular/core';
import { CtBatchMetaData } from '@ps-gui/claims-terminal-shared';
import { BehandlerAssignmentService } from '@ps-gui/shared-document-preview';

import { cleanValues } from './clean-extracted-values';
import { mapForderungspositionen } from './forderungsposition.mapper';
import { CtBatchStoreService } from '../app-state/ct-batch-store.service';
import { PersistDataService } from '../app-state/persist-data.service';
import {
  CompleteEingangCt2Dto,
  ConsolidatedDataDto,
  DocumentCompletionDto,
  SplitCompletionDto,
  TransformEingangCtBatchDto,
} from '../../models/interfaces';
import {
  ExtractedDataSnapshot,
  ExtractedRow,
  ExtractedTableCell,
  ExtractedTablesByKey,
  ExtractedValue,
  ExtractedValuesByKey,
} from '../../models/extracted-values';
import { ct2ConfigurationByBatchDomain } from '../../models/extraction-models/extraction-models';
import { formatDate } from '../../utils/Date.utils';

@Injectable({ providedIn: 'root' })
export class EingangCompletionBuilder {
  private readonly persistDataService = inject(PersistDataService);
  private readonly ctBatchStoreService = inject(CtBatchStoreService);
  private readonly behandlerAssignmentService = inject(BehandlerAssignmentService);

  async loadSnapshotAndBuildDto(metaData: CtBatchMetaData | undefined): Promise<{
    snapshot: ExtractedDataSnapshot;
    dto: CompleteEingangCt2Dto;
  }> {
    const snapshot = await this.persistDataService.getConsolidatedDataSnapshot(0);
    const dokumente = await this.buildDokumente();
    const splits = await this.buildSplits(snapshot);
    return {
      snapshot,
      dto: {
        behandlers: this.persistDataService.behandlerData,
        consolidatedData: this.mapConsolidatedData(snapshot, metaData),
        dokumente,
        splits,
      },
    };
  }

  buildTransformDto(
    eingangId: string,
    auditId: string,
    auditRequestId: string,
    snapshot: ExtractedDataSnapshot,
  ): TransformEingangCtBatchDto {
    return {
      eingangId,
      auditId,
      auditRequestId,
      claimNumber: (snapshot.fields['kopfdaten_schadennummer'] as string) ?? '',
      businessTransaction: (snapshot.fields['kopfdaten_kennung'] as string) ?? '',
      tenant: (snapshot.fields['tenant'] as string) ?? '',
      proofType: (snapshot.fields['auftragsart'] as string) ?? '',
      orderDescription: null,
    };
  }

  private async buildSplits(firstSnapshot: ExtractedDataSnapshot): Promise<SplitCompletionDto[]> {
    const splitDocumentIds = this.getSplitDocumentIds();

    const splits: SplitCompletionDto[] = [
      {
        splitIndex: 0,
        splitDocumentId: splitDocumentIds[0] ?? null,
        forderungspositionen: mapForderungspositionen(firstSnapshot),
      },
    ];

    for (let splitIndex = 1; splitIndex < splitDocumentIds.length; splitIndex++) {
      const snapshot = await this.persistDataService.getConsolidatedDataSnapshot(splitIndex);
      splits.push({
        splitIndex,
        splitDocumentId: splitDocumentIds[splitIndex],
        forderungspositionen: mapForderungspositionen(snapshot),
      });
    }

    return splits;
  }

  private getSplitDocumentIds(): string[] {
    const splitInducingDocumentClass =
      ct2ConfigurationByBatchDomain[this.ctBatchStoreService.ctBatchDomain].batchSplitConfiguration
        ?.splitInducingDocumentClass;

    return this.ctBatchStoreService.ctBatch.documents
      .filter((document) => document.documentClasses.includes(splitInducingDocumentClass))
      .map((document) => document.id);
  }

  private mapConsolidatedData(
    snapshot: ExtractedDataSnapshot,
    metaData?: CtBatchMetaData,
  ): ConsolidatedDataDto {
    const fields = cleanValues(snapshot.fields);
    return {
      betroffenePersonVorname: (fields['kopfdaten_vorname_betroffene_person'] as string) ?? '',
      betroffenePersonNachname: (fields['kopfdaten_nachname_betroffene_person'] as string) ?? '',
      betroffenePersonGeburtsdatum: formatDate(fields['kopfdaten_geburtsdatum'] as string) ?? '',
      tenant: (fields['tenant'] as string) ?? '',
      auftragsart: (fields['auftragsart'] as string) ?? '',
      schadenNummer: (fields['kopfdaten_schadennummer'] as string) ?? '',
      forderungDatum: formatDate(fields['kopfdaten_forderungsdatum'] as string),
      schadenDatum: formatDate(fields['kopfdaten_schadendatum'] as string) ?? '',
      kennung: (fields['kopfdaten_kennung'] as string) ?? null,
      sachbearbeiterNachname: (fields['sachbearbeiter_nachname'] as string) ?? null,
      sachbearbeiterVorname: (fields['sachbearbeiter_vorname'] as string) ?? null,
      sachbearbeiterEmail: (fields['sachbearbeiter_email'] as string) ?? null,
      sachbearbeiterTelefon: (fields['sachbearbeiter_telefon'] as string) ?? null,
      kostentraegerName: textOrNull(fields['kostentraeger_name']),
      kostentraegerStrasse: textOrNull(fields['kostentraeger_strasse']),
      kostentraegerHausnummer: textOrNull(fields['kostentraeger_hausnummer']),
      kostentraegerPostleitzahl: textOrNull(fields['kostentraeger_postleitzahl']),
      kostentraegerStadt: textOrNull(fields['kostentraeger_stadt']),
      kostentraegerAktenzeichen: textOrNull(fields['kopfdaten_kostentraegerzeichen']),
      externeId: metaData?.externeId,
      nachricht:
        metaData?.initialeNachricht ?? (fields['kopfdaten_vyda_nachricht'] as string) ?? null,
    };
  }

  private async buildDokumente(): Promise<DocumentCompletionDto[]> {
    const documents = this.ctBatchStoreService.ctBatch.documents;
    return Promise.all(
      documents.map(async (document): Promise<DocumentCompletionDto> => {
        const merged: ExtractedDataSnapshot = { fields: {}, tables: {} };
        for (const documentClass of document.documentClasses) {
          const snap = await this.persistDataService.getExtractedDocumentDataSnapshot(
            document.id,
            documentClass,
          );
          Object.assign(merged.fields, snap.fields);
          Object.assign(merged.tables, snap.tables);
        }

        this.reformatFieldDate(merged.fields, 'kopfdaten_forderungsdatum');
        this.reformatFieldDate(merged.fields, 'kopfdaten_schadendatum');
        this.reformatFieldDate(merged.fields, 'kopfdaten_geburtsdatum');
        this.reformatFieldDate(merged.fields, '301er_daten_aufnahmedatum');
        this.reformatFieldDate(merged.fields, '301er_daten_entlassungsdatum');
        this.reformatFieldDate(merged.fields, '301er_daten_geburtsdatum_geschaedigter');
        this.reformatFieldDate(merged.fields, 'attest_schadendatum');
        this.reformatFieldDate(merged.fields, 'attest_betroffene_person_geburtsdatum');
        this.reformatFieldDate(merged.fields, 'ese_schadendatum');
        this.reformatFieldDate(merged.fields, 'ese_betroffene_person_geburtsdatum');
        this.reformatFieldDate(merged.fields, 'page1_behandlung_von');
        this.reformatFieldDate(merged.fields, 'page1_behandlung_bis');
        this.reformatFieldDate(merged.fields, 'page1_beschwerden_aufgetreten_am');
        this.reformatFieldDate(merged.fields, 'page1_arbeitsunfaehigkeit_von');
        this.reformatFieldDate(merged.fields, 'page1_arbeitsunfaehigkeit_bis');
        this.reformatFieldDate(merged.fields, 'page2_behandlung_datum');
        this.reformatFieldDate(merged.fields, 'page3_datum_letzte_konsultation');
        this.reformatFieldDate(merged.fields, 'page4_datum_abschaetzung_langfristige_folgen');
        this.reformatFieldDate(merged.fields, 'document_creation_date');

        Object.values(merged.tables).forEach((rows: ExtractedRow[]) => {
          for (const row of rows) {
            this.reformatTableCellDate(row.rowData['forderungen_von']);
            this.reformatTableCellDate(row.rowData['forderungen_bis']);
            this.reformatTableCellDate(row.rowData['operationen_und_prozeduren_leistungsdatum']);
            this.reformatTableCellDate(row.rowData['page2_behandlung_datum']);
          }
        });

        const extractedData = {
          fields: cleanValues(merged.fields),
          tables: this.flattenTableRows(merged.tables),
        };

        return {
          documentCreationDate: merged.fields['document_creation_date'] as string,
          documentId: document.id,
          extractedData: JSON.stringify(extractedData),
          assignedBehandlerIndex: this.behandlerAssignmentService.getBehandlerAssignmentData(
            document.id,
          )?.behandlerIndex,
        };
      }),
    );
  }

  private reformatFieldDate(valuesByKey: ExtractedValuesByKey, fieldName: string) {
    const dateString = valuesByKey[fieldName];
    if (dateString) {
      valuesByKey[fieldName] = formatDate(dateString as string);
    } else if (dateString === '') {
      valuesByKey[fieldName] = null;
    }
  }

  private reformatTableCellDate(tableCell: ExtractedTableCell | undefined) {
    if (!tableCell) {
      return;
    }

    const dateString = tableCell.value;
    if (dateString) {
      tableCell.value = formatDate(dateString as string);
    } else if (tableCell.value === '') {
      tableCell.value = null;
    }
  }

  private flattenTableRows(
    tables: ExtractedTablesByKey,
  ): Record<string, Record<string, unknown>[]> {
    const tableItems: Record<string, Record<string, unknown>[]> = {};

    Object.entries(tables).forEach(([tableName, rows]) => {
      tableItems[tableName] = rows.map((row) => {
        const rowItem: Record<string, unknown> = {};

        Object.entries(row.rowData).forEach(([key, value]) => {
          rowItem[key] = value.value;
        });

        rowItem['class'] = row.class;

        return rowItem;
      });
    });

    return tableItems;
  }
}

/** An untouched form field is an empty string; the backend needs a null to tell "not captured". */
const textOrNull = (value: ExtractedValue | undefined): string | null => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();

  return text === '' ? null : text;
};
